import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, X, InstagramLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export function PublicHeader({ onDonateClick }) {
  const [open, setOpen] = useState(false);
  const [branding, setBranding] = useState({ header_title: "SEVA", header_subtitle: "Voiceless Animals", instagram_handle: "seva.animals" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings/public").then((r) => { if (r.data?.branding) setBranding(r.data.branding); }).catch(() => {});
  }, []);

  const NAV = [
    { label: "Home", href: "https://seva-india.org/", external: true },
    { label: "About Us", href: "https://seva-india.org/#about", external: true },
    { label: "Contact Us", href: "https://seva-india.org/#contact", external: true },
    { label: "Donate Monthly", to: "/donate-monthly", external: false, testid: "nav-donate-monthly" },
    { label: "Download Receipt", to: "/receipts", external: false, testid: "nav-download-receipt" },
  ];

  const handleDonate = async (e) => {
    e?.preventDefault?.();
    setOpen(false);
    if (onDonateClick) return onDonateClick();
    try {
      const { data } = await api.get("/campaigns");
      const first = (data || [])[0];
      if (first) nav(`/campaigns/${first.slug}?donate=1`);
    } catch {}
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FBF9F6]/90 border-b border-[#DCE5D4]/70" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-3 flex items-center justify-between gap-3">
        <a href="https://seva-india.org/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="header-logo">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt={branding.header_title} className="h-10 w-10 sm:h-12 sm:w-12 object-contain shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="font-display font-bold text-base sm:text-lg text-[#2F7A4A]">{branding.header_title}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#56624F] truncate">{branding.header_subtitle}</div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-[#1F2A1C]">
          {NAV.map((n, i) => n.external ? (
            <a key={i} href={n.href} target="_blank" rel="noopener noreferrer" className="hover:text-[#4FAC39]" data-testid={"nav-" + n.label.toLowerCase().replace(/\s+/g, "-")}>{n.label}</a>
          ) : (
            <Link key={i} to={n.to} className="hover:text-[#4FAC39]" data-testid={n.testid}>{n.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={`https://instagram.com/${branding.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex p-2 rounded-full hover:bg-[#EEF4E9] text-[#4FAC39]" aria-label="Instagram" data-testid="header-instagram">
            <InstagramLogo size={20} weight="fill" />
          </a>
          <button onClick={handleDonate} className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm" data-testid="header-donate-btn">Donate Now</button>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-lg" data-testid="menu-toggle">{open ? <X size={22} /> : <List size={22} />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#DCE5D4] bg-white px-5 py-3 space-y-3">
          {NAV.map((n, i) => n.external ? (
            <a key={i} href={n.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="block text-sm" data-testid={"mobile-nav-" + n.label.toLowerCase().replace(/\s+/g, "-")}>{n.label}</a>
          ) : (
            <Link key={i} to={n.to} onClick={() => setOpen(false)} className="block text-sm" data-testid={"mobile-" + (n.testid || "")}>{n.label}</Link>
          ))}
          <button onClick={handleDonate} className="btn-primary w-full !py-3" data-testid="mobile-header-donate">Donate Now</button>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  const [branding, setBranding] = useState({ header_title: "SEVA", header_subtitle: "Voiceless Animals", instagram_handle: "seva.animals" });
  useEffect(() => { api.get("/settings/public").then((r) => { if (r.data?.branding) setBranding(r.data.branding); }).catch(() => {}); }, []);
  return (
    <footer className="bg-[#1F2A1C] text-[#DCE5D4] mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="SEVA" className="h-14 w-14 object-contain bg-white rounded-full p-1" />
            <div>
              <div className="font-display font-bold text-xl text-white">{branding.header_title}</div>
              <div className="text-xs uppercase tracking-widest text-[#98A38F]">{branding.header_subtitle}</div>
            </div>
          </div>
          <p className="text-sm text-[#98A38F] max-w-md leading-relaxed">
            A registered non-profit rescuing, treating and sheltering stray &amp; injured animals. 100% of your donation goes directly to the animals. 80G tax benefits available.
          </p>
          <a href={`https://instagram.com/${branding.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-[#4FAC39] hover:text-white">
            <InstagramLogo size={18} weight="fill" /> Follow us for updates on our cases &mdash; @{branding.instagram_handle}
          </a>
        </div>
        <div>
          <h4 className="font-display font-bold mb-3 text-white">Quick Links</h4>
          <a href="https://seva-india.org/" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">Home</a>
          <a href="https://seva-india.org/#about" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">About Us</a>
          <a href="https://seva-india.org/#contact" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">Contact Us</a>
          <Link to="/donate-monthly" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">Donate Monthly</Link>
          <Link to="/receipts" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">Download Receipt</Link>
        </div>
        <div>
          <h4 className="font-display font-bold mb-3 text-white">Contact</h4>
          <a href="mailto:seva.animals@gmail.com" className="block text-sm text-[#98A38F] hover:text-[#4FAC39] break-all">seva.animals@gmail.com</a>
          <a href="https://wa.me/+91 60036 90847" className="block text-sm text-[#98A38F] hover:text-[#4FAC39]">WhatsApp: +91 60036 90847</a>
          <p className="text-sm text-[#98A38F] mt-2">Guwahati, Assam, India</p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-[#768070]">
        &copy; {new Date().getFullYear()} SEVA Animal Welfare. All rights reserved.
      </div>
    </footer>
  );
}
